"use client"

import React from "react"
import { DebouncedInput } from "@/components/ui/debounced-input"
import { Columns3, Download, Loader2, Plus, RefreshCw, SlidersHorizontal } from "lucide-react"
import { useExplorerContext } from "@/components/explorer/explorer-context"
import { activeFilterCount } from "@/lib/filter-values"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

export interface ExplorerToolbarProps {
  onOpenFilters: () => void
  onOpenFields: () => void
  onAddEntityToMapping: () => void
  onPull: () => void
}

/**
 * Timesheet Hub pill toolbar: search, Filters (n), Fields (n), refresh, Pull, Add to
 * Mapping, and the total on the right. Records load live, so there is no Load button.
 */
export function ExplorerToolbar({ onOpenFilters, onOpenFields, onAddEntityToMapping, onPull }: ExplorerToolbarProps) {
  const { selectedEntity, activeEntity, searchText, setSearchText, filterValues, selectedFields, fieldSchema, isLoadingRecords, isLoadingEntityDetail, page, reload, blocker } = useExplorerContext()
  const { t, n } = useTranslation()
  if (!selectedEntity) return null

  const filtersActive = activeFilterCount(filterValues)

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 pt-3 pb-2">
      {selectedEntity.supportsKeywordSearch && (
        <DebouncedInput
          value={searchText}
          onChange={(value) => setSearchText(String(value))}
          placeholder={t("explorer.toolbar.searchPlaceholder", { entity: selectedEntity.displayName.toLowerCase() })}
          className="h-9 w-64 rounded-[9px] border-control-border text-[13px]"
        />
      )}
      {(activeEntity?.supportsFilters ?? false) && (
        <ToolbarPill active={filtersActive > 0} onClick={onOpenFilters} icon={<SlidersHorizontal className="h-3.5 w-3.5" />}>
          {t("explorer.filters.title")}
          {filtersActive > 0 ? ` · ${n(filtersActive)}` : ""}
          {isLoadingEntityDetail && <Loader2 className="ml-1 h-3 w-3 animate-spin" />}
        </ToolbarPill>
      )}
      {/* Reachable whenever it can do something: a vendor that needs a selection, columns already
          chosen (so they can be cleared), or a schema to choose from */}
      {(selectedEntity.supportsFieldSelection || selectedFields.length > 0 || (fieldSchema.fields?.length ?? 0) > 0) && (
        <ToolbarPill active={false} onClick={onOpenFields} icon={<Columns3 className="h-3.5 w-3.5" />}>
          {t("explorer.fields.title")} · {n(selectedFields.length)}
        </ToolbarPill>
      )}
      <ToolbarPill active={false} onClick={reload} disabled={isLoadingRecords || blocker !== null} icon={isLoadingRecords ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} title={t("explorer.toolbar.reloadTitle")}>
        {isLoadingRecords ? t("explorer.toolbar.loading") : t("common.actions.refresh")}
      </ToolbarPill>
      <ToolbarPill active={false} onClick={onPull} icon={<Download className="h-3.5 w-3.5" />} title={t("explorer.toolbar.pullTitle")}>
        {t("explorer.toolbar.pull")}
      </ToolbarPill>
      {selectedEntity.supportsDetailFetch && (
        <ToolbarPill active={false} onClick={onAddEntityToMapping} icon={<Plus className="h-3.5 w-3.5" />}>
          {t("explorer.toolbar.addToMapping")}
        </ToolbarPill>
      )}
      {page?.totalCount != null && (
        <span className="ml-auto text-xs text-muted-foreground">
          <span className="font-display text-sm font-bold text-foreground">{n(page.totalCount)}</span> {t("explorer.toolbar.total")}
        </span>
      )}
    </div>
  )
}

function ToolbarPill({ active, onClick, icon, children, disabled, title }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode; disabled?: boolean; title?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-9 items-center gap-1.5 rounded-[9px] border px-3 text-[13px] font-medium transition-colors disabled:opacity-50 ${
        active
          ? "border-[var(--th-active-pill-border)] bg-accent text-accent-foreground"
          : "border-control-border bg-card text-[var(--gw-text-body)] hover:border-[var(--th-control-border-hover)]"
      }`}
    >
      {icon}
      {children}
    </button>
  )
}
